import { useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Text } from '@antimatterhq/ui/components/typography';
import { deepEqual } from '@/lib/datasource';
import { DatasourceUpgradePreserveUnreferencedColumnsDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { Datasource_UnreferencedColumns } from '@/types/api';

export const DatasourceUpgradeDiffPreserveUnreferencedColumns = ({
  diff,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  diff: DatasourceUpgradePreserveUnreferencedColumnsDiff;
  onAccept: () => void;
  onReject: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}) => {
  const currentValueKept = useMemo(
    () =>
      diff.status === 'confirmed' &&
      deepEqual(diff.currentValue, diff.upgradeValue),
    [diff.currentValue, diff.status, diff.upgradeValue],
  );

  const upgradePresetValueApplied = useMemo(
    () =>
      diff.status === 'confirmed' &&
      deepEqual(diff.upgradePresetValue, diff.upgradeValue),
    [diff.status, diff.upgradePresetValue, diff.upgradeValue],
  );

  // Helper to format Datasource_UnreferencedColumns
  const renderUnreferencedColumns = (
    value?: Datasource_UnreferencedColumns,
  ) => {
    if (!value || typeof value !== 'object') {
      return <span className="italic text-gray-500">no value</span>;
    }
    const {
      preserve,
      embedColumn,
      embedColumnType,
      duplicatePrefix,
      omitColumns,
    } = value;
    const embed = Boolean(embedColumn);
    return (
      <div className="text-left">
        <div>
          <b>Preserve:</b> {preserve ? 'Yes' : 'No'}
        </div>
        {preserve && (
          <>
            <div>
              <b>Embed:</b> {embed ? 'Yes' : 'No'}
            </div>
            {embed && (
              <>
                <div>
                  <b>Embed Column:</b>{' '}
                  {embedColumn || (
                    <span className="italic text-gray-500">none</span>
                  )}
                </div>
                <div>
                  <b>Embed Column Type:</b>{' '}
                  {embedColumnType || (
                    <span className="italic text-gray-500">none</span>
                  )}
                </div>
              </>
            )}
            {!embed && (
              <div>
                <b>Duplicate Prefix:</b>{' '}
                {duplicatePrefix || (
                  <span className="italic text-gray-500">none</span>
                )}
              </div>
            )}
            <div>
              <b>Omit Columns:</b>{' '}
              {omitColumns &&
              omitColumns.length > 0 &&
              omitColumns.some(Boolean) ? (
                omitColumns.filter(Boolean).join(', ')
              ) : (
                <span className="italic text-gray-500">none</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`fieldspec-diff-preserveunreferencedcolumns-root-${diff.title}`}
    >
      <div className="flex w-full">
        <div className="flex-1 text-center">
          <div className="flex items-stretch">
            <div className="flex-1"></div>
            <div>
              <Text
                bold
                data-testid="fieldspec-diff-preserveunreferencedcolumns-current-value"
              >
                {renderUnreferencedColumns(diff.currentValue)}
              </Text>
            </div>

            <div className="flex-1">
              {currentValueKept && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-preserveunreferencedcolumns-rejected-icon"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 text-center">
          <div className="flex items-stretch">
            <div className="flex-1"></div>
            <div>
              <Text
                bold
                data-testid="fieldspec-diff-preserveunreferencedcolumns-upgrade-value"
              >
                {renderUnreferencedColumns(diff.upgradePresetValue)}
              </Text>
            </div>
            <div className="flex-1">
              {upgradePresetValueApplied && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-preserveunreferencedcolumns-accepted-icon"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          <Button
            variant="default"
            onClick={onReject}
            disabled={currentValueKept || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-preserveunreferencedcolumns-reject-btn"
          >
            {!upgradePresetValueApplied && 'Keep existing version'}
            {upgradePresetValueApplied && 'Revert to existing version'}
          </Button>
        </div>
        <div className="flex-1 text-center">
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={upgradePresetValueApplied || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-preserveunreferencedcolumns-accept-btn"
          >
            Apply pending version
          </Button>
        </div>
      </div>
    </div>
  );
};
